/**
 * messageParsers.ts
 *
 * 메시지 바디 파싱 순수 함수 모음.
 *
 * 동일한 파싱 로직이 useMomentForm(임시저장 불러오기)과
 * MomentReservePage(미리보기 props 생성) 양쪽에 중복 정의되어 있었습니다.
 * 이 파일에서 단일 관리하고 두 곳 모두 import하여 사용합니다.
 *
 * 규칙:
 * - 모든 함수는 순수 함수입니다. 외부 상태를 읽거나 변경하지 않습니다.
 * - 새로운 메시지 타입이 추가될 때 이 파일만 수정하면 됩니다.
 */

import {
  createMomentButton,
  createMomentCarouselItem,
  createMomentCommerceIntro,
  createMomentCoupon,
  createMomentListItem,
  MOMENT_MESSAGE_TYPE_OPTIONS,
  type MomentButton,
  type MomentCarouselItem,
  type MomentCommerceIntro,
  type MomentCoupon,
  type MomentListItem,
  type MomentMessageType,
} from "../data/momentOptions";
import type { MomentAdDetailDto } from "../api/momentApi";

// ── 원시값 변환 헬퍼 ─────────────────────────────────────────────
// 서버 응답은 unknown 타입으로 받으므로, 안전하게 원하는 타입으로 변환합니다.

/**
 * unknown 값을 string으로 변환합니다.
 * string이 아닌 값(undefined, null, number 등)은 빈 문자열("")을 반환합니다.
 */
export function toStringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * unknown 값을 boolean으로 변환합니다.
 * 정확히 true인 경우에만 true를 반환하며, 나머지는 모두 false입니다.
 */
export function toBooleanValue(value: unknown): boolean {
  return value === true;
}

// ── 메시지 바디 구성요소 파싱 ──────────────────────────────────────
// 각 함수는 서버의 messageBody 필드 하나를 받아 프론트엔드 모델로 변환합니다.

/**
 * 버튼 배열을 파싱합니다.
 * 값이 없거나 빈 배열이면 기본 버튼 1개(button-1)를 반환합니다.
 */
export function parseButtons(value: unknown): MomentButton[] {
  if (!Array.isArray(value) || value.length === 0) return [createMomentButton("button-1")];
  return (value as Array<Record<string, unknown>>).map((button, index) => ({
    id: `button-${index + 1}`,
    title: toStringValue(button.title),
    pcLandingUrl: toStringValue(button.pcLandingUrl),
    mobileLandingUrl: toStringValue(button.mobileLandingUrl),
  }));
}

/**
 * 쿠폰 객체를 파싱합니다.
 * null/undefined이면 couponBookTitleType을 "UPGRADE"로 하는 기본 쿠폰을 반환합니다.
 */
export function parseCoupon(value: unknown): MomentCoupon {
  const c = (value as Record<string, unknown>) ?? {};
  return {
    couponBookTitleType: (c.couponBookTitleType as MomentCoupon["couponBookTitleType"]) ?? "UPGRADE",
    couponBookTitle: toStringValue(c.couponBookTitle),
    title: toStringValue(c.title),
    pcLandingUrl: toStringValue(c.pcLandingUrl),
    mobileLandingUrl: toStringValue(c.mobileLandingUrl),
  };
}

/**
 * 와이드 리스트 아이템 배열을 파싱합니다.
 * 값이 없거나 빈 배열이면 기본 3개 아이템(list-1~3)을 반환합니다.
 * landing 필드 안에 pcLandingUrl / mobileLandingUrl이 중첩 구조로 있음에 주의합니다.
 */
export function parseListItems(value: unknown): MomentListItem[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [createMomentListItem("list-1"), createMomentListItem("list-2"), createMomentListItem("list-3")];
  }
  return (value as Array<Record<string, unknown>>).map((item, index) => {
    const landing = ((item.landing as Record<string, unknown>) ?? {});
    const imageUrl = toStringValue(item.imageUrl);
    return {
      id: `list-${index + 1}`,
      description: toStringValue(item.description),
      imagePreview: imageUrl || null,
      imageName: imageUrl ? imageUrl.split("/").pop() ?? `list-${index + 1}` : null,
      imageFile: null,
      imageUploadedUrl: imageUrl,
      pcLandingUrl: toStringValue(landing.pcLandingUrl),
      mobileLandingUrl: toStringValue(landing.mobileLandingUrl),
    };
  });
}

/**
 * 캐러셀 아이템 배열을 파싱합니다.
 * 값이 없거나 빈 배열이면 기본 2개 아이템(carousel-1~2)을 반환합니다.
 *
 * 서버 구조:
 * - landing: 카드 전체 랜딩 URL
 * - buttons[0]: 첫 번째 버튼 (buttonTitle 등)
 * - buttons[1]: 두 번째 버튼 (secondaryButton)
 * - couponBook: 쿠폰 (있을 때만)
 *
 * @param type 메시지 유형 (향후 커머스/피드 분기 처리 시 사용)
 */
export function parseCarouselItems(value: unknown, type: MomentMessageType): MomentCarouselItem[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [createMomentCarouselItem("carousel-1"), createMomentCarouselItem("carousel-2")];
  }
  return (value as Array<Record<string, unknown>>).map((item, index) => {
    const landing = ((item.landing as Record<string, unknown>) ?? {});
    const btns = Array.isArray(item.buttons) ? (item.buttons as Array<Record<string, unknown>>) : [];
    const firstButton = btns[0] ?? {};
    const secondButton = btns[1] ?? {};
    const imageUrl = toStringValue(item.imageUrl);
    return {
      id: `carousel-${index + 1}`,
      messageTitle: toStringValue(item.messageTitle),
      description: toStringValue(item.description),
      imagePreview: imageUrl || null,
      imageName: imageUrl ? imageUrl.split("/").pop() ?? `carousel-${index + 1}` : null,
      imageFile: null,
      imageUploadedUrl: imageUrl,
      pcLandingUrl: toStringValue(landing.pcLandingUrl),
      mobileLandingUrl: toStringValue(landing.mobileLandingUrl),
      buttonTitle: toStringValue(firstButton.title),
      buttonPcLandingUrl: toStringValue(firstButton.pcLandingUrl),
      buttonMobileLandingUrl: toStringValue(firstButton.mobileLandingUrl),
      secondaryButtonTitle: toStringValue(secondButton.title),
      secondaryButtonPcLandingUrl: toStringValue(secondButton.pcLandingUrl),
      secondaryButtonMobileLandingUrl: toStringValue(secondButton.mobileLandingUrl),
      // 두 번째 버튼 필드 중 하나라도 값이 있으면 활성화된 것으로 복원합니다.
      secondaryButtonEnabled: !!(
        toStringValue(secondButton.title) ||
        toStringValue(secondButton.mobileLandingUrl) ||
        toStringValue(secondButton.pcLandingUrl)
      ),
      priceAmount: toStringValue(item.priceAmount),
      discountedPriceAmount: toStringValue(item.discountedPriceAmount),
      shareFlag: toBooleanValue(item.shareFlag),
      couponEnabled: !!item.couponBook,
      coupon: item.couponBook ? parseCoupon(item.couponBook) : createMomentCoupon(),
    };
  });
}

/**
 * 캐러셀 커머스 인트로 카드를 파싱합니다.
 * messageBody.introCarousel 필드에 담긴 객체를 받습니다.
 */
export function parseCommerceIntro(value: unknown): MomentCommerceIntro {
  const intro = (value as Record<string, unknown>) ?? {};
  const landing = ((intro.landing as Record<string, unknown>) ?? {});
  const imageUrl = toStringValue(intro.imageUrl);
  return {
    messageTitle: toStringValue(intro.messageTitle),
    description: toStringValue(intro.description),
    imagePreview: imageUrl || null,
    imageName: imageUrl ? imageUrl.split("/").pop() ?? "intro-image" : null,
    imageFile: null,
    imageUploadedUrl: imageUrl,
    pcLandingUrl: toStringValue(landing.pcLandingUrl),
    mobileLandingUrl: toStringValue(landing.mobileLandingUrl),
  };
}

// ── 미리보기 props 빌더 ───────────────────────────────────────────

/**
 * MomentAdDetailDto를 MomentPhonePreview 컴포넌트의 props로 변환합니다.
 * MomentReservePage에서 서버 응답을 미리보기 컴포넌트에 바인딩할 때 사용합니다.
 *
 * 위의 파싱 함수들을 조합하여 messageBody 전체를 한 번에 변환하는 진입점입니다.
 */
export function buildPreviewProps(ad: MomentAdDetailDto) {
  const message = ad.messageBody ?? {};
  const draftType = (message.type as MomentMessageType) ?? (ad.messageType as MomentMessageType);
  const typeOption =
    MOMENT_MESSAGE_TYPE_OPTIONS.find((o) => o.value === draftType) ?? MOMENT_MESSAGE_TYPE_OPTIONS[0];

  const parsedButtons = parseButtons(message.buttons);
  // 버튼 데이터(title/URL)가 하나라도 있으면 버튼 표시 상태로 복원합니다.
  const buttonsEnabled = parsedButtons.some(
    (b) => b.title.trim() || b.mobileLandingUrl.trim() || b.pcLandingUrl.trim()
  );

  // 와이드 이미지 타입일 때만 items[0]에서 이미지 URL을 읽습니다.
  let imagePreview: string | null = null;
  if (draftType === "WIDE_MESSAGE") {
    const items = Array.isArray(message.items) ? (message.items as Array<Record<string, unknown>>) : [];
    imagePreview = toStringValue(items[0]?.imageUrl) || null;
  }

  const listItems =
    draftType === "WIDE_LIST_MESSAGE"
      ? parseListItems(message.items)
      : [createMomentListItem("list-1"), createMomentListItem("list-2"), createMomentListItem("list-3")];

  const isCarousel = draftType === "CAROUSEL_FEED_MESSAGE" || draftType === "CAROUSEL_COMMERCE_MESSAGE";
  const carouselItems = isCarousel
    ? parseCarouselItems(message.carousels, draftType)
    : [createMomentCarouselItem("carousel-1"), createMomentCarouselItem("carousel-2")];

  // 인트로 카드는 커머스 타입이고 introCarousel 필드가 있을 때만 활성화합니다.
  const commerceIntroEnabled = draftType === "CAROUSEL_COMMERCE_MESSAGE" && !!message.introCarousel;
  const commerceIntro = commerceIntroEnabled
    ? parseCommerceIntro(message.introCarousel)
    : createMomentCommerceIntro();

  return {
    channelLabel: ad.channelName ?? "카카오톡 채널",
    messageName: ad.adName,
    body: toStringValue(message.description),
    messageTitle: toStringValue(message.messageTitle),
    imagePreview,
    typeOption,
    messageType: draftType,
    buttons: parsedButtons,
    buttonsEnabled,
    ageAuthEnabled: toBooleanValue(message.ageAuthEnabled),
    adFlag: toBooleanValue(message.adFlag),
    shareFlag: toBooleanValue(message.shareFlag),
    couponEnabled: !!message.couponBook,
    coupon: message.couponBook ? parseCoupon(message.couponBook) : createMomentCoupon(),
    moreButtonEnabled: !!message.moreButton,
    listItems,
    carouselItems,
    commerceIntroEnabled,
    commerceIntro,
  };
}
